<template>

  <v-container fluid class="px-0 order-driver-message-manager">

    <!--------------------------------------------->
    <!-- Messages to Driver (OrderDriverMessage) -->
    <!--------------------------------------------->
    <v-row class="mt-4" v-if="allowSendMessages">
      <!-- <v-col cols="12" xs="12" sm="12" md="5" lg="3" class="d-flex align-center justify-center"> -->
      <v-col cols="12" class="d-flex align-center justify-start">
        <v-btn
            outlined
            color="amber darken-2"
            @click="isMessageDialogOpen = true">
          Add Message for Driver
        </v-btn>
      </v-col>
    </v-row>

    <v-container fluid class="pb-0" v-if="driverMessageItems.length > 0">
      <!-- Message List Header -->
      <v-row class="mt-4 pb-1 message-list-header">
        <v-col cols="12" xs="12" sm="4" md="3" lg="3">
          Added
        </v-col>
        <v-col cols="12" xs="12" sm="5" md="5" lg="6">
          Message
        </v-col>
        <v-col cols="12" xs="12" sm="3" md="4" lg="3" class="d-flex align-center justify-center">
          <span>Viewed by Driver</span>
        </v-col>
      </v-row>

      <!-- Message List Content -->
      <v-row class="mb-6 message-item-container" :class="{ 'not-seen': !item.is_confirmed }" v-for="(item, index) in driverMessageItems" :key="`driver-message-${index}`">
        <v-col cols="12" xs="12" sm="4" md="3" lg="3" class="d-flex align-center">
          <div :class="item.is_confirmed ? 'added-date-seen' : 'added-date'">{{ formatDateTime(item.created_at) }}</div>
        </v-col>
        <v-col cols="12" xs="12" sm="5" md="5" lg="6" class="d-flex align-center">
          <div :class="item.is_confirmed ? 'message-text-seen' : 'message-text'">{{ item.message }}</div>
        </v-col>
        <v-col cols="12" xs="12" sm="3" md="4" lg="3" class="d-flex align-center justify-center">
          <div class="confirmed-date d-flex align-center" v-if="item.is_confirmed">
            <v-icon style="font-size:18px;color:#727272;" class="pr-2">mdi-eye-outline</v-icon>
            {{ formatDateTime(item.confirmed_at) }}
          </div>
          <template v-else-if="canEditMessages">
            <v-btn
                icon
                color="primary"
                class="mr-8"
                @click="editMessage(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <dialog-confirm-button
                buttonStyle="icon"
                message="Delete this unseen driver message?"
                icon="mdi-trash-can-outline"
                iconColor="red accent-2"
                v-on:confirmed="deleteMessage(item)" />
          </template>
        </v-col>
      </v-row>
    </v-container>

    <v-row class="mt-1" v-else-if="!allowSendMessages">
      <v-col class="pl-8">
        <div class="no-messages-text">(No message)</div>
      </v-col>
    </v-row>

    <!------------------------->
    <!-- Send Message Dialog -->
    <!------------------------->
    <v-dialog persistent width="660" v-model="isMessageDialogOpen">
      <v-card>
        <!-- Title -->
        <v-card-title class="text-h5">
          <span v-if="isEditing">Edit Message for Driver</span>
          <span v-else>Add Message for Driver</span>
        </v-card-title>

        <v-card-text class="mt-3 px-6 pb-2">
          <!-- Order details -->
          <div class="mt-3 d-flex align-center dialog-text">
            <div class="dialog-field-label">Order:</div>
            <div class="dialog-order-number">{{ order.order_number }}</div>
            <div class="px-4">-</div>
            <div class="">
              <v-chip label outlined small :class="order.order_status">
                {{ formatStatusLabel(order.order_status) }}
              </v-chip>
            </div>
          </div>
          <div class="mt-3 d-flex align-center dialog-text">
            <div class="dialog-field-label">Driver:</div>
            <div class="dialog-driver-name" v-if="driverName">{{ driverName }}</div>
            <div class="dialog-driver-name-not-assigned" v-else>(not assigned)</div>
          </div>
          <!-- <div class="mt-3 d-flex align-center dialog-text">
            <div class="dialog-field-label">Shipper:</div>
            <div class="dialog-shipper-name">{{ order.shipper.name }}</div>
          </div> -->
          <div class="mt-3 d-flex align-center dialog-text">
            <div class="dialog-field-label">Receiver:</div>
            <div class="dialog-receiver-name">{{ order.receiver.name }}</div>
          </div>
          <div class="mt-8">
            <v-textarea
                rows="5"
                outlined
                v-model.trim="message"
                :counter="maxChars"
                :rules="[v => !v || v.length <= maxChars || `Maximum ${maxChars} characters allowed`]"
                persistent-hint />
          </div>
          <!-- Step selection -->
          <div class="d-flex align-center" v-if="allowStepSelection">
            <div class="pl-2 pr-8 dialog-order-step-field-label">Show at:</div>
            <v-checkbox
                hide-details
                class="mt-0 pt-0 mr-10"
                label="Pickup"
                value="pickup"
                v-model="orderSteps"
                @change="validateStepSelections('dropoff')">
              <template v-slot:label>
                <div class="dialog-order-step-label">Pickup</div>
              </template>
            </v-checkbox>
            <v-checkbox
                hide-details
                class="mt-0 pt-0"
                label="Dropoff"
                value="dropoff"
                v-model="orderSteps"
                @change="validateStepSelections('pickup')">
              <template v-slot:label>
                <div class="dialog-order-step-label">Dropoff</div>
              </template>
            </v-checkbox>
          </div>
          <div class="dialog-info-text d-flex align-center" v-else>
            <v-icon color="amber darken-1" style="font-size:21px;" class="pr-2">mdi-information</v-icon>
            The message will be shown on pickup and dropoff steps.
          </div>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions class="mt-4">
          <v-btn
              large
              plain
              color="primary"
              class="ml-2 px-5"
              @click="close">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
              v-if="isEditing"
              large
              color="primary"
              class="mb-2 mr-2 px-5"
              :disabled="!message"
              @click="updateMessage">
            Save Message
          </v-btn>
          <v-btn
              v-else
              large
              color="primary"
              class="mb-2 mr-2 px-5"
              :disabled="!message"
              @click="sendMessage">
            Add Message
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
import DialogConfirmButton from '@/components/app/DialogConfirmButton'

export default {
  name: 'OrderDriverMessageManager',

  components: {
    DialogConfirmButton
  },

  props: {
    order: {
      type: Object,
      required: true
    },
    route: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      allowStepSelection: false,
      // loaded messages
      isDataLoading: false,
      dataLoadingError: null,
      driverMessageData: null,
      // ---------- send message
      maxChars: 400,
      isMessageDialogOpen: false,
      message: null,
      defaultOrderSteps: ['pickup', 'dropoff'],
      orderSteps: ['pickup', 'dropoff'],
      isSending: false,
      sendError: null,
      // ---------- edit message
      isEditing: false,
      editingMessage: null,
      // ---------- delete message
      isDeleting: false,
      deleteError: null
    }
  },

  computed: {
    driverName () {
      return (this.route && this.route.driver)
        ? this.route.driver.display_name
        : null
    },
    driverMessageItems () {
      return (this.driverMessageData) ? this.driverMessageData.items : []
    },
    canEditMessages () {
      return this.order.order_status !== 'DELIVERED' &&
        this.order.order_status !== 'CANCELED'
    },
    allowSendMessages () {
      return this.driverMessageItems.length === 0 &&
        this.order.order_status !== 'DELIVERED' &&
        this.order.order_status !== 'CANCELED'
    }
  },

  async beforeMount () {
    await this.loadMessages()
  },

  methods: {
    // Display a date and time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D, YYYY - h:mma') : ''
    },

    // Display status label
    formatStatusLabel (statusCode) {
      return (statusCode === 'REJECTED_ON_DELIVERY') ? 'REJECTED' : statusCode
    },

    reset () {
      this.message = null
      this.orderSteps = this.defaultOrderSteps
      this.sendError = null
      this.deleteError = null
    },

    close () {
      this.reset()
      this.isMessageDialogOpen = false
      this.isEditing = false
      this.editingMessage = null
    },

    // getMessagePreview (messageText) {
    //   const preview = (text, maxChars) => {
    //     if (text.length <= maxChars) return text

    //     // Find the last space within maxChars limit
    //     const lastSpace = text.substring(0, maxChars).lastIndexOf(' ')

    //     // If no space found, just cut at maxChars
    //     if (lastSpace === -1) return text.substring(0, maxChars) + '...'

    //     // Cut at the last space and add ellipsis
    //     return text.substring(0, lastSpace) + '...'
    //   }

    //   return preview(messageText, 80) // Adjust maxChars value as needed
    // },

    // Handles appropriate selection of steps (ensures at least one is checked)
    validateStepSelections (valueToSet) {
      if (this.orderSteps.length === 0) this.orderSteps.push(valueToSet)
    },

    // Open message dialag to edit a message
    editMessage (item = {}) {
      this.isEditing = true
      this.editingMessage = item
      this.message = item.message
      this.orderSteps = (item.order_steps) ? item.order_steps.split(',') : this.defaultOrderSteps
      this.isMessageDialogOpen = true
    },

    // Add message for driver
    async sendMessage () {
      if (!this.message) return false
      this.isSending = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order/${this.order.id}/driver-message`,
          opts: {
            bodyData: {
              message: this.message,
              order_steps: this.orderSteps.join(',')
            }
          }
        })

        // Reload data
        this.loadMessages()
        this.close()
      } catch (err) {
        this.sendError = err.message || err.text || err
      } finally {
        this.isSending = false
      }
    },

    // Update message to driver
    async updateMessage () {
      if (!this.message || !this.editingMessage) return false
      this.isSending = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: `order-driver-message/${this.editingMessage.id}`,
          opts: {
            bodyData: {
              message: this.message,
              order_steps: this.orderSteps.join(',')
            }
          }
        })

        // Reload data
        this.loadMessages()
        this.close()
      } catch (err) {
        this.sendError = err.message || err.text || err
      } finally {
        this.isSending = false
      }
    },

    // Delete message
    async deleteMessage (item = {}) {
      if (!item.id) return false
      this.isDeleting = true

      try {
        await this.$store.dispatch('sh/dataApi', {
          method: 'delete',
          uri: `order-driver-message/${item.id}`
        })

        // Reload data
        this.loadMessages()
      } catch (err) {
        this.deleteError = err.message || err.text || err
      } finally {
        this.isDeleting = false
      }
    },

    async loadMessages () {
      this.isDataLoading = true

      try {
        this.driverMessageData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.order.id}/driver-messages`
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style scoped>
  .order-driver-message-manager .info-text {
    color: #9c9c9c;
    font-size: 1.08rem;
    font-style: italic;
  }

  .order-driver-message-manager .message-list-header {
    color: #9c9c9c;
    max-width: 900px;
  }

  .order-driver-message-manager .message-item-container {
    border: 1px solid #525252;
    border-radius: 4px;
    max-width: 900px;
  }

  .order-driver-message-manager .message-item-container.not-seen {
    border: 1px solid #a2a2a2;
  }

  .order-driver-message-manager .added-date {
    color: #b2b2b2;
    font-size: 0.9rem;
  }

  .order-driver-message-manager .added-date-seen {
    color: #9c9c9c;
    font-size: 0.9rem;
  }

  .order-driver-message-manager .message-text-seen {
    color: #9c9c9c;
  }

  .order-driver-message-manager .not-seen-text {
    color: #727272;
    font-style: italic;
  }

  .order-driver-message-manager .no-messages-text {
    color: #9c9c9c;
  }

  .order-driver-message-manager .confirmed-date {
    color: #727272;
    font-size: 0.9rem;
  }

  /* Dialog style */
  .dialog-text {
    color: #9c9c9c;
    font-size: 1.0rem;
  }
  .dialog-info-text {
    color: #c9c9c9;
    font-size: 1.0rem;
  }
  .dialog-field-label {
    width: 80px;
  }
  .dialog-order-number {
    font-size: 1.08rem;
    color: #f7a725;
  }
  .dialog-driver-name {
    color: #f2f2f2;
  }
  .dialog-driver-name-not-assigned {
    font-size: 0.94rem;
    color: #727272;
  }
  .dialog-shipper-name {
    color: #f2f2f2;
  }
  .dialog-receiver-name {
    color: #f2f2f2;
  }
  .dialog-order-step-field-label {
    color: #9c9c9c;
    font-size: 1.0rem;
  }
  .dialog-order-step-label {
    color: #ffffff;
  }

  a.item-link {
    text-decoration: none;
  }

  a.item-link:hover {
    text-decoration: underline;
  }
</style>
